import { useEffect, useRef } from "react";

/**
 * Priorities for Ionic's `ionBackButton` (Android hardware back). Ionic runs the
 * single highest-priority handler; on a tie the most recently registered wins.
 * For reference: Ionic overlays (IonModal, sheets, alerts) use 100, the menu 99,
 * the settings IonNav stack 10, page navigation 0.
 */
export const BACK_BUTTON_PRIORITY = {
  /** Small pop-up dialogs (plain overlays). Above Ionic sheets they may open from. */
  dialog: 101,
  /** Full-screen settings layers. They can host Ionic sheets, which must win. */
  fullScreenLayer: 20,
} as const;

interface Options {
  /** When false, back is swallowed (nothing behind moves) but the dialog stays open. */
  canClose?: boolean;
  priority?: number;
}

/**
 * Makes a plain overlay (one that isn't an Ionic modal) close on Android back,
 * like an IonModal does. Ionic doesn't know about these overlays, so without
 * this, back skips them and navigates the page underneath.
 */
export function useCloseOnBackButton(
  isOpen: boolean,
  onClose: () => void,
  { canClose = true, priority = BACK_BUTTON_PRIORITY.dialog }: Options = {},
) {
  // Read the latest values at press time so the listener isn't re-added on
  // every render (callers usually pass inline arrows).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const canCloseRef = useRef(canClose);
  canCloseRef.current = canClose;

  useEffect(() => {
    if (!isOpen) return;
    const handler = (ev: Event) => {
      (ev as CustomEvent<{ register: (priority: number, fn: () => void) => void }>).detail.register(
        priority,
        () => {
          if (canCloseRef.current) onCloseRef.current();
        },
      );
    };
    document.addEventListener("ionBackButton", handler);
    return () => document.removeEventListener("ionBackButton", handler);
  }, [isOpen, priority]);
}
